import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';

import { AppError, ErrorCode } from '../models/api-error';

/**
 * Single source of truth for turning an HttpErrorResponse into a user-facing
 * {@link AppError}. Replaces the ad-hoc `error.error.message` /
 * `error.error?.error` / `error.message` extraction scattered across the
 * interceptor and NgRx effects (findings B4).
 *
 * Backend error envelopes (see wolsal_core/utils/response.py):
 *   - error:       { message: string, code: string }
 *   - validation:  { error: [{ name?, error }], code: 'VALIDATION_ERROR', message? }
 *
 * This service also distinguishes failures that never carried a usable body —
 * network outages (status 0) and server errors (5xx) — and substitutes a
 * localized, actionable message instead of leaking a generic 'Internal Error'.
 */
@Injectable({ providedIn: 'root' })
export class ErrorMessageService {
  constructor(private readonly translate: TranslateService) {}

  parse(error: HttpErrorResponse): AppError {
    const status = error?.status ?? 0;
    const body = error?.error;

    // 1. The request never reached the server (offline, CORS, DNS, timeout).
    if (status === 0) {
      return {
        messages: this.t('common.toaster.network_error'),
        code: ErrorCode.NETWORK_ERROR,
        status,
      };
    }

    // 2. Field-level validation errors: { error: [{ name?, error }], code }.
    if (body && Array.isArray(body.error) && body.error.length) {
      const fieldErrors = body.error as Array<{ name?: string; error: string }>;
      const messages = fieldErrors.map((e) => e?.error).filter((m): m is string => !!m);
      return {
        messages: messages.length ? messages : this.t('common.toaster.generic_error'),
        code: body.code ?? ErrorCode.VALIDATION_ERROR,
        status,
        fieldErrors,
      };
    }

    // 3. Standard error envelope: { message, code }.
    const code = (body && typeof body.code === 'string' && body.code) || null;
    const bodyMessage =
      body && typeof body.message === 'string' && body.message.trim() ? body.message : null;

    // Never echo a server error / missing body to the user — use a localized,
    // class-appropriate fallback so the toast is meaningful instead of cryptic.
    const message =
      bodyMessage ??
      (status >= 500
        ? this.t('common.toaster.server_error')
        : this.t('common.toaster.generic_error'));

    return { messages: message, code, status };
  }

  private t(key: string): string {
    return this.translate.instant(key);
  }
}
